import {
  Directive,
  ElementRef,
  EmbeddedViewRef,
  HostListener,
  Input,
  OnDestroy,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';

@Directive({
  selector: '[tooltip],[tooltipContent]',
  standalone: false,
})
export class TooltipDirective implements OnDestroy {
  @Input() tooltip?: string | null;
  @Input() tooltipTitle?: string | null;
  @Input() tooltipAction?: string | null;
  @Input() tooltipContent?: TemplateRef<any> | null;
  @Input() tooltipSide: 'top' | 'bottom' | 'left' | 'right' = 'top';
  @Input() tooltipClass?: string;
  @Input() tooltipDisabled = false;

  private static activeOverlayElement?: HTMLElement;
  private static activeOverlayCleanup?: () => void;

  private overlayElement?: HTMLElement;
  private tooltipEmbeddedView?: EmbeddedViewRef<unknown>;
  private openDelayTimer?: ReturnType<typeof setTimeout>;
  private animationFrameId?: number;
  private scrollHandler?: () => void;

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private viewContainerRef: ViewContainerRef,
  ) {}

  ngOnDestroy(): void {
    this.cancelPendingOpen();
    this.hide();
  }

  @HostListener('mouseenter')
  onMouseEnter(): void {
    this.cancelPendingOpen();
    this.openDelayTimer = setTimeout(() => this.show(), 100);
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    this.cancelPendingOpen();
    this.hide();
  }

  @HostListener('focusin')
  onFocusIn(): void {
    this.cancelPendingOpen();
    this.openDelayTimer = setTimeout(() => this.show(), 100);
  }

  @HostListener('focusout')
  onFocusOut(): void {
    this.cancelPendingOpen();
    this.hide();
  }
  @HostListener('click')
  onClick(): void {
    this.elementRef.nativeElement.blur();
    this.cancelPendingOpen();
    this.hide();
  }

  private cancelPendingOpen(): void {
    if (this.openDelayTimer) {
      clearTimeout(this.openDelayTimer);
      this.openDelayTimer = undefined;
    }
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = undefined;
    }
  }

  private show(): void {
    if (this.tooltipDisabled || !(this.tooltipContent || this.tooltip?.trim()) || this.overlayElement) {
      return;
    }

    const wrapperElement = document.createElement('div');
    wrapperElement.className = 'app-tooltip-overlay';
    if (this.tooltipClass) {
      wrapperElement.classList.add(this.tooltipClass);
    }

    const hostElement = this.elementRef.nativeElement;
    if (this.tooltip?.trim() === 'Sort' && hostElement.classList.contains('grid-header-sort')) {
      if (hostElement.classList.contains('sort-active') && hostElement.querySelector("ion-icon[name='arrow-down']")) {
        wrapperElement.classList.add('-sort-ascending');
      } else {
        wrapperElement.classList.add('-sort-descending');
      }
    }

    if (this.tooltipContent) {
      wrapperElement.classList.add('app-tooltip-overlay--rich');
      if (this.tooltipTitle) {
        const titleElement = document.createElement('div');
        titleElement.className = 'app-tooltip-overlay-title';
        titleElement.textContent = this.tooltipTitle;
        wrapperElement.appendChild(titleElement);
      }
      const bodyElement = document.createElement('div');
      bodyElement.className = 'app-tooltip-overlay-body';
      this.tooltipEmbeddedView = this.viewContainerRef.createEmbeddedView(this.tooltipContent);
      this.tooltipEmbeddedView.detectChanges();
      for (const node of this.tooltipEmbeddedView.rootNodes) {
        bodyElement.appendChild(node as Node);
      }
      wrapperElement.appendChild(bodyElement);
    } else if (this.tooltipAction) {
      wrapperElement.classList.add('app-tooltip-overlay--action-hint');

      const textElement = document.createElement('div');
      textElement.className = 'app-tooltip-overlay-text';
      textElement.textContent = this.tooltip ?? '';
      wrapperElement.appendChild(textElement);

      const actionElement = document.createElement('div');
      actionElement.className = 'app-tooltip-overlay-action';
      actionElement.textContent = this.tooltipAction;
      wrapperElement.appendChild(actionElement);
    } else {
      wrapperElement.textContent = this.tooltip ?? '';
    }

    if (TooltipDirective.activeOverlayElement && TooltipDirective.activeOverlayElement !== wrapperElement) {
      const previousOverlay = TooltipDirective.activeOverlayElement;
      TooltipDirective.activeOverlayElement = undefined;
      TooltipDirective.activeOverlayCleanup?.();
      TooltipDirective.activeOverlayCleanup = undefined;
      previousOverlay.remove();
    }

    document.body.appendChild(wrapperElement);
    wrapperElement.style.visibility = 'hidden';
    this.overlayElement = wrapperElement;
    TooltipDirective.activeOverlayElement = wrapperElement;
    TooltipDirective.activeOverlayCleanup = () => this.destroyEmbeddedView();

    this.scrollHandler = () => {
      this.cancelPendingOpen();
      this.hide();
    };
    window.addEventListener('scroll', this.scrollHandler, { capture: true, passive: true });

    this.animationFrameId = requestAnimationFrame(() => {
      if (!this.overlayElement) {
        return;
      }

      const triggerRect = this.elementRef.nativeElement.getBoundingClientRect();
      const tooltipRect = this.overlayElement.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const gapBetweenTriggerAndTooltip = 6;
      const edgePadding = 8;

      let resolvedSide = this.tooltipSide;
      if (resolvedSide === 'top' && triggerRect.top - tooltipRect.height - gapBetweenTriggerAndTooltip < edgePadding)
        resolvedSide = 'bottom';
      if (
        resolvedSide === 'bottom' &&
        triggerRect.bottom + tooltipRect.height + gapBetweenTriggerAndTooltip > viewportHeight - edgePadding
      )
        resolvedSide = 'top';
      if (resolvedSide === 'left' && triggerRect.left - tooltipRect.width - gapBetweenTriggerAndTooltip < edgePadding)
        resolvedSide = 'right';
      if (
        resolvedSide === 'right' &&
        triggerRect.right + tooltipRect.width + gapBetweenTriggerAndTooltip > viewportWidth - edgePadding
      )
        resolvedSide = 'left';

      let top: number;
      let left: number;

      if (resolvedSide === 'bottom') {
        top = triggerRect.bottom + gapBetweenTriggerAndTooltip;
        left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
      } else if (resolvedSide === 'left') {
        top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
        left = triggerRect.left - tooltipRect.width - gapBetweenTriggerAndTooltip;
      } else if (resolvedSide === 'right') {
        top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
        left = triggerRect.right + gapBetweenTriggerAndTooltip;
      } else {
        top = triggerRect.top - tooltipRect.height - gapBetweenTriggerAndTooltip;
        left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
      }

      left = Math.max(edgePadding, Math.min(left, viewportWidth - tooltipRect.width - edgePadding));
      top = Math.max(edgePadding, Math.min(top, viewportHeight - tooltipRect.height - edgePadding));

      this.overlayElement.style.left = `${Math.round(left)}px`;
      this.overlayElement.style.top = `${Math.round(top)}px`;
      this.overlayElement.style.visibility = '';
    });
  }

  private hide(): void {
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler, true);
      this.scrollHandler = undefined;
    }
    const elementToRemove = this.overlayElement;
    if (!elementToRemove) {
      return;
    }
    this.overlayElement = undefined;
    if (TooltipDirective.activeOverlayElement === elementToRemove) {
      TooltipDirective.activeOverlayElement = undefined;
      TooltipDirective.activeOverlayCleanup = undefined;
    }
    this.destroyEmbeddedView();
    elementToRemove.style.animation = 'tooltip-out 90ms ease-in forwards';
    elementToRemove.addEventListener('animationend', () => elementToRemove.remove(), { once: true });
  }

  private destroyEmbeddedView(): void {
    this.tooltipEmbeddedView?.destroy();
    this.tooltipEmbeddedView = undefined;
  }
}
